import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Apartment } from '@mui/icons-material';
import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';

import {
  collapsibleItemSx,
  DropdownArrow,
  getCollapsibleButtonSx,
  StyledNavButtonOutlined
} from '@/components/Layout/Sidebar';
import { SIDEBAR_COLORS } from '@/components/Layout/sidebarTheme';
import {
  getFacilityTypeGroupOptions,
  getGenericAllLabelForType
} from '@/hooks/facility-selection/screenerFacilityTypeHelpers';
import { useFacilitySelectorController } from '@/hooks/facility-selection/useFacilitySelectorController';
import { useScreenerFacilityView } from '@/hooks/facility-selection/useScreenerFacilityView';
import { getScreenerSettingValue } from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALL_FACILITIES,
  selectedFacilityIdAtom,
  useFacilitiesQuery
} from '@/hooks/useFacilitiesQuery';
import { ScreenerSettingName } from '@/pages/CRM/Screener/hooks/useScreenerSettingsQuery';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';

import FacilitySelectorPopover from './FacilitySelectorPopover';
import { getUserFacility } from './getUserFacilityHelper';

const FacilitySelector = ({ expanded }: { expanded: boolean }) => {
  const { view, selectedFacilityId } = useScreenerFacilityView();
  const { selectFacility } = useFacilitySelectorController();
  const [, setSelectedFacilityId] = useAtom(selectedFacilityIdAtom);
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();

  const location = useLocation();
  const [isCRM, setIsCRM] = useState(false);
  const currentUser = useCurrentUser().data!;
  const { data: facilitiesResponse, isLoading: isLoadingFacilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const hasMultipleFacilities = currentUser.user_facility_accesses!.length > 1;

  const facilityIds = useMemo(
    () =>
      isCRM ? (facilitiesResponse?.facilities.map((f) => f.id) ?? []) : [],
    [facilitiesResponse, isCRM]
  );

  const billingDisabledFacilityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const id of facilityIds) {
      if (
        getScreenerSettingValue(
          ScreenerSettingName.BillingScreenerEnabled,
          id
        ) === 'disabled'
      ) {
        ids.add(id);
      }
    }
    return ids;
  }, [facilityIds]);

  const anyFacilityBillingDisabled = billingDisabledFacilityIds.size > 0;

  const onSelectFacility = (facilityId: string) => {
    selectFacility(facilityId);
    setIsOpen(false);
  };

  const userFacility = useMemo(() => {
    return getUserFacility(
      selectedFacilityId,
      facilitiesResponse,
      currentUser,
      setSelectedFacilityId
    );
  }, [
    selectedFacilityId,
    setSelectedFacilityId,
    facilitiesResponse,
    currentUser
  ]);

  useEffect(() => {
    setIsCRM(location.pathname.startsWith('/crm'));
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.isFirstLogin) {
      onSelectFacility(currentUser!.facility_id);
    }
  }, [location.state, currentUser]);

  const accessibleFacilityIds = useMemo(
    () =>
      new Set(
        currentUser.user_facility_accesses?.map((a) => a.facility_id) ?? []
      ),
    [currentUser.user_facility_accesses]
  );
  const groupOptions = useMemo(
    () =>
      getFacilityTypeGroupOptions(
        facilitiesResponse?.facilities ?? [],
        accessibleFacilityIds
      ),
    [facilitiesResponse?.facilities, accessibleFacilityIds]
  );

  if (isLoadingFacilitiesResponse) {
    return (
      <Stack sx={{ width: '100%', paddingY: 1 }} alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  const isAllFacilities =
    selectedFacilityId === ALL_FACILITIES || isAllFacilitiesSelected;

  const activeGroup =
    view.kind === 'all-facilities'
      ? groupOptions.find((o) => o.facilityType === view.facilityType)
      : null;

  const isMultiType = groupOptions.length > 1;
  const allFacilitiesLabel =
    view.kind === 'all-facilities'
      ? isMultiType
        ? (activeGroup?.label ?? getGenericAllLabelForType(view.facilityType))
        : getGenericAllLabelForType(view.facilityType)
      : 'All Facilities';

  const facilityCount = isAllFacilities ? activeGroup?.facilityIds.length : 1;

  const facilityCountLabel =
    facilityCount !== undefined && facilityCount > 99 ? '99+' : facilityCount;

  return (
    <>
      <Tooltip
        title={
          expanded
            ? ''
            : isAllFacilities
              ? allFacilitiesLabel
              : (userFacility?.name ?? '')
        }
        placement="right"
        disableInteractive
      >
        <span style={{ display: 'inline-flex', width: '100%' }}>
          <StyledNavButtonOutlined
            data-testid="community-selector"
            ref={ref}
            disabled={!hasMultipleFacilities}
            onClick={() => hasMultipleFacilities && setIsOpen(true)}
            sx={getCollapsibleButtonSx(expanded)}
          >
            <ListItemIcon sx={collapsibleItemSx}>
              <Stack alignItems="center">
                <Apartment sx={{ fontSize: '20px' }} />
                {!expanded && facilityCountLabel !== undefined && (
                  <Typography
                    sx={{
                      fontSize: '10px',
                      lineHeight: '14px',
                      fontWeight: 500,
                      color: SIDEBAR_COLORS.textSecondary
                    }}
                  >
                    {facilityCountLabel}
                  </Typography>
                )}
              </Stack>
            </ListItemIcon>
            {expanded && (
              <>
                <ListItemText
                  primary={
                    isAllFacilities ? allFacilitiesLabel : userFacility?.name
                  }
                  secondary={!isAllFacilities && userFacility?.address}
                />
                {hasMultipleFacilities && <DropdownArrow isOpen={isOpen} />}
              </>
            )}
          </StyledNavButtonOutlined>
        </span>
      </Tooltip>
      <FacilitySelectorPopover
        user={currentUser}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        anchorEl={ref.current!}
        onSelectFacility={onSelectFacility}
        selectedFacilityId={selectedFacilityId}
        isCRM={isCRM}
        canSelectAllFacilities={
          facilitiesResponse?.can_select_all_facilities || false
        }
        billingDisabledFacilityIds={billingDisabledFacilityIds}
        anyFacilityBillingDisabled={anyFacilityBillingDisabled}
        facilities={facilitiesResponse?.facilities ?? []}
        accessibleFacilityIds={accessibleFacilityIds}
      />
    </>
  );
};

export default FacilitySelector;
